import React, { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useFadeInOnScroll } from '../../../hooks/useFadeInOnScroll';
import { AnimatedContainer, SectionHeader, EnhancedSectionTitle, SectionSubtitle } from '../../styles/sharedComponents';
import {
  TechnologySection,
  TechGrid,
  TechContent,
  TechFeature,
  TechIcon,
  TechText,
  TechTitle,
  TechDescription,
  TechVisual
} from './styles';

// Lazy load heavy component
const MiniNucleoNeural = lazy(() => import('../../../components/MiniNeuralVault'));

const Technology: React.FC = () => {
  const { t } = useTranslation();
  const [technologyRef, technologyVisible] = useFadeInOnScroll<HTMLElement>();

  return (
    <TechnologySection ref={technologyRef} id="technology" $isVisible={technologyVisible}>
      <AnimatedContainer $isVisible={technologyVisible}>
        <SectionHeader>
          <EnhancedSectionTitle>{t('technology.title')}</EnhancedSectionTitle>
          <SectionSubtitle>
            {t('technology.subtitle')}
          </SectionSubtitle>
        </SectionHeader>

        <TechGrid>
          <TechContent>
            <TechFeature $isVisible={technologyVisible} $delay={200}>
              <TechIcon>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </TechIcon>
              <TechText>
                <TechTitle>{t('technology.items.claude.title')}</TechTitle>
                <TechDescription>
                  {t('technology.items.claude.description')}
                </TechDescription>
              </TechText>
            </TechFeature>

            <TechFeature $isVisible={technologyVisible} $delay={400}>
              <TechIcon>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </TechIcon>
              <TechText>
                <TechTitle>{t('technology.items.context.title')}</TechTitle>
                <TechDescription>
                  {t('technology.items.context.description')}
                </TechDescription>
              </TechText>
            </TechFeature>

            <TechFeature $isVisible={technologyVisible} $delay={600}>
              <TechIcon>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </TechIcon>
              <TechText>
                <TechTitle>{t('technology.items.realtime.title')}</TechTitle>
                <TechDescription>
                  {t('technology.items.realtime.description')}
                </TechDescription>
              </TechText>
            </TechFeature>

            <TechFeature $isVisible={technologyVisible} $delay={400}>
              <TechIcon>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </TechIcon>
              <TechText>
                <TechTitle>{t('technology.items.security.title')}</TechTitle>
                <TechDescription>
                  {t('technology.items.security.description')}
                </TechDescription>
              </TechText>
            </TechFeature>
          </TechContent>

          <TechVisual $isVisible={technologyVisible}>
            <Suspense fallback={<div style={{ width: '100%', height: '400px', background: 'transparent' }} />}>
              <MiniNucleoNeural />
            </Suspense>
          </TechVisual>
        </TechGrid>
      </AnimatedContainer>
    </TechnologySection>
  );
};

export default Technology;
